//eslint:disable max-func-body-length
import { toISODatewithoutTimezone } from 'app/infrastructure/data/date';
import { CodeActionType, CodeItemSubsetWire, CodeItemWire, CodeToWire } from '../types';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { CodetableFeatures, CodetableFeature, CodetableMetadata } from 'app/area/codetable/types-store';
import { CodeComplexValue } from 'app/area/shared/codetable/types';
import { SupportedCodeScreens } from 'app/area/codetable/item/screen-renderer/types';
import { CodeItem } from 'app/area/codetable/item/types-store';
import { defaultUomConversionFactor } from 'app/area/codetable/item/constants';
import { MapToZeroValuePerScreenItem, MapToZeroValuePerScreenItems } from 'app/area/codetable/item/mapping/types';

const mapLanguageToWire = (codeToWire: CodeToWire, property: keyof CodetableFeatures) => {
  const { codeItem, features } = codeToWire;

  if (!features || !features[property]) {
    return undefined;
  }

  return codeItem[property];
};

const mapLanguages = (codeToWire: CodeToWire) => {
  const descriptionToWire = mapLanguageToWire(codeToWire, CodetableFeature.description);
  const longDescriptionToWire = mapLanguageToWire(codeToWire, CodetableFeature.longDescription);

  const descriptionLanguageIds = descriptionToWire &&
    descriptionToWire.map(description => description.value) || [];
  const longDescriptionLanguageIds = longDescriptionToWire &&
    longDescriptionToWire.map(longDescription => longDescription.value)
      .filter(longDescriptionId => !descriptionLanguageIds.some(id => id === longDescriptionId)) || [];

  const languageIds = [
    ...descriptionLanguageIds,
    ...longDescriptionLanguageIds
  ];

  return languageIds.length > 0
    ? languageIds.map(id => {

      const description = descriptionToWire?.find(d => d.value === id)?.description;
      const longDescription = longDescriptionToWire?.find(d => d.value === id)?.description;
      return {
        v: id,
        ...(!!description && {
          d: description
        }),
        ...(!!longDescription && {
          ld: longDescription
        })
      };
    })
    : undefined;
};

export const mapSubsetsToWire = (codeToWire: CodeToWire): CodeItemSubsetWire => {

  const { codeItem } = codeToWire;

  return {
    ...(!!codeItem.userGroups && {
      subset: codeItem.userGroups.map(ug => ({
        v: ug.value
      }))
    })
  };
};

export const mapCodeToWire = (codeToWire: CodeToWire): CodeItemWire => {

  const { codeItem, codetableId } = codeToWire;

  const languages = mapLanguages(codeToWire);

  return {

    ...(isDefinedIncludingZero(codeItem.id) && {
      code: {
        v: codeItem.id
      }
    }),

    ...(!!codeItem.disabled ? {
      disabled: toISODatewithoutTimezone(codeItem.disabled)()
    } : { disabled: null }),

    ...(isDefinedIncludingZero(codeItem.colour) && {
      decimalColour: codeItem.colour
    }),

    ...(!!codeItem.icon && {
      icon: codeItem.icon
    }),

    ...(!!codeItem.residualIcon && {
      icon2: codeItem.residualIcon
    }),

    ...(!!codeItem.category && {
      category: {
        v: codeItem.category.value,
        ...(!!codeItem.category?.disabled) && {
          disabled: toISODatewithoutTimezone(codeItem.category.disabled)()
        }
      }
    }),

    ...('sequence' in codeItem && {
      sequence:
        isDefinedIncludingZero(codeItem.sequence)
          ? codeItem.sequence
          : null
    }),

    ...(!!languages && {
      language: languages
    }),

    ...(!!codeItem.userGroups && {
      subset: codeItem.userGroups.map(ug => ({
        v: ug.value
      }))
    }),

    ...(!!codeItem.excludeFields && {
      exclude: codeItem.excludeFields.map(e => ({
        v: e.value
      }))
    }),

    ...(!!codeItem.codetableTypes && {
      class: codeItem.codetableTypes.map(e => ({
        v: e.value
      }))
    }),

    ...(!!codeItem.caseTypes && {
      application: codeItem.caseTypes.map(c => ({
        v: c.value
      }))
    }),

    ...('parent' in codeItem && {
      parent:
        isDefinedIncludingZero(codeItem.parent)
          ? {
            v: codeItem.parent.value
          }
          : null
    }),

    ...(!!codeItem.node && {
      node: {
        v: codeItem.node.value
      }
    }),

    ...(!!codeItem.colourCode && {
      colourCode: {
        v: codeItem.colourCode.value,
        d: codeItem.colourCode.description,
        decimalColour: codeItem.colourCode.backgroundColor
      }
    }),

    ...(codeItem.companyRegisterId !== undefined && {
      companyRegisterId: codeItem.companyRegisterId
    }),

    ...('scoreCalcWeight' in codeItem && {
      scoreCalcWeight:
        isDefinedIncludingZero(codeItem.scoreCalcWeight)
          ? codeItem.scoreCalcWeight
          : null
    }),

    ...(!!codeItem.severity && {
      severity: {
        v: codeItem.severity.value
      }
    }),

    ...(!!codeItem.uom && {
      uom: {
        v: codeItem.uom.value
      }
    }),

    ...(isDefinedIncludingZero(codeItem.low) && {
      low: codeItem.low
    }),

    ...(isDefinedIncludingZero(codeItem.mid) && {
      mid: codeItem.mid
    }),

    ...(isDefinedIncludingZero(codeItem.high) && {
      high: codeItem.high
    }),

    ...(!!codeItem.recurrence && {
      recurrence: {
        v: codeItem.recurrence.value
      }
    }),

    ...(!!codeItem.likelihood && {
      likelihood: {
        v: codeItem.likelihood.value
      }
    }),

    ...(isDefinedIncludingZero(codeItem.minLevel) && {
      minLevel: codeItem.minLevel
    }),

    ...(isDefinedIncludingZero(codeItem.level) && {
      level: codeItem.level
    }),

    ...(!!codeItem.checkedCode && {
      checkedCode: {
        v: codeItem.checkedCode.value
      }
    }),

    ...(!!codeItem.uncheckedCode && {
      uncheckedCode: {
        v: codeItem.uncheckedCode.value
      }
    }),

    ...(isDefinedIncludingZero(codeItem.rangeFrom) && {
      rangeFrom: codeItem.rangeFrom
    }),

    ...(isDefinedIncludingZero(codeItem.rangeTo) && {
      rangeTo: codeItem.rangeTo
    }),

    ...(!!codeItem.defaultUom && {
      defaultUom: {
        v: codeItem.defaultUom.value
      }
    }),

    ...(!!codeItem.defaultUomType && {
      defaultUomType: {
        v: codeItem.defaultUomType.value
      }
    }),

    ...(!!codeItem.consequenceType && {
      consequenceType: {
        v: codeItem.consequenceType.value
      }
    }),

    ...(!!codeItem.uomTypeFrom && {
      uomTypeFrom: {
        v: codeItem.uomTypeFrom.value
      }
    }),

    ...(!!codeItem.uomTypeTo && {
      uomTypeTo: {
        v: codeItem.uomTypeTo.value
      }
    }),

    ...(!!codeItem.substanceUomSet && !!codeToWire.substanceUomSetType && {
      [codeToWire.substanceUomSetType]: codeItem.substanceUomSet.map(a => ({
        uomSet: {
          v: a.uomSet.value,
          d: a.uomSet.description
        },
        uom: {
          v: a.uom.value,
          d: a.uom.description,
          ...(a.uom.longDescription && { ld: a.uom.longDescription })
        },
        uomType: {
          v: a.uomType.value,
          d: a.uomType.description,
          ...(a.uomType.longDescription && { longDescription: a.uomType.longDescription })
        },
        uomIndicator: {
          v: a.uomIndicator?.value || 0,
          d: a.uomIndicator?.description,
          ...(a.uomIndicator?.longDescription && { longDescription: a.uomIndicator.longDescription })
        },
        uomTypeIndicator: {
          v: a.uomTypeIndicator?.value || 0,
          d: a.uomTypeIndicator?.description,
          ...(a.uomTypeIndicator?.longDescription && { longDescription: a.uomTypeIndicator.longDescription })
        }
      }))
    }),

    ...(codetableId === SupportedCodeScreens.SubstanceHierarchy && (codeItem.substanceConversionFactor?.length > 0 ? {
      substanceConversionFactor: codeItem.substanceConversionFactor.map(conversionFactor => ({
        uomConversionType: {
          v: conversionFactor.uomConversionType.value,
          d: conversionFactor.uomConversionType.description
        },
        uomTypeFrom: {
          v: conversionFactor.uomTypeFrom.value,
          d: conversionFactor.uomTypeFrom.description
        },
        uomFrom: {
          v: conversionFactor.uomFrom.value,
          d: conversionFactor.uomFrom.description,
          ...(conversionFactor.uomFrom.longDescription && { longDescription: conversionFactor.uomFrom.longDescription })
        },
        uomTypeTo: {
          v: conversionFactor.uomTypeTo.value,
          d: conversionFactor.uomTypeTo.description
        },
        uomTo: {
          v: conversionFactor.uomTo.value,
          d: conversionFactor.uomTo.description,
          ...(conversionFactor.uomTo.longDescription && { longDescription: conversionFactor.uomTo.longDescription })
        },
        ...(isDefinedIncludingZero(conversionFactor.factor) && { factor: conversionFactor.factor })
      }))
    } : { substanceConversionFactor: [] })),

    ...(codetableId === SupportedCodeScreens.GasType && (codeItem.safetyLimit?.length > 0 ? {
      safetyLimit: codeItem.safetyLimit.map(item => ({
        application: {
          v: item.application.value,
          d: item.application.description
        },
        ...(item.minValue && { minValue: item.minValue }),
        ...(item.maxValue && { maxValue: item.maxValue }),
        ...(isDefinedIncludingZero(item.warningDeviationPercent) && { warningDeviationPercent: item.warningDeviationPercent })
      }))
    } : { safetyLimit: [] })),

    ...(codetableId === SupportedCodeScreens.UnitOfMeasure && {
      measurementSystem: {
        v: codeItem?.measurementSystem?.value ?? 0
      }
    }),

    ...(!!codeItem.uomConversion && {
      uomConversion: codeItem.uomConversion
        .map(item => {
          return {
            ...(codeItem?.id && { uomFrom: { v: codeItem?.id } }),
            uomTo: { v: item.value },
            factor: item.factor
          };
        })
    })
  };
};

export const mapColourCode = (metadata: CodetableMetadata, codeItem: CodeItem): CodeComplexValue<number> =>
  mapToZeroValueIfEmpty(codeItem.colourCode, isDefinedIncludingZero(metadata?.colourCode?.id));

export const mapToZeroValuesPerScreens = (items: MapToZeroValuePerScreenItems = {}): Partial<CodeItem> =>
  Object.assign({},
    ...Object.keys(items).map(property => ({
      [property]: mapToZeroValuePerScreens(items[property])
    })));

const mapToZeroValuePerScreens = ({ codetableId, value, supportedScreens }: MapToZeroValuePerScreenItem):
  CodeComplexValue<number> => mapToZeroValueIfEmpty(value, supportedScreens.includes(codetableId));

export const mapToZeroValueIfEmpty = (value: CodeComplexValue<number>, shouldMapToZeroValue: boolean): CodeComplexValue<number> => {
  if (!shouldMapToZeroValue) {
    return undefined;
  }

  if (isDefinedIncludingZero(value?.value)) {
    return value;
  }

  return {
    value: 0
  };
};

export const mapUomConversion = (
  id: number,
  actionType: CodeActionType,
  isUomExcluded: boolean,
  initialData: CodeComplexValue<number>[],
  uomConversionFactors: CodeComplexValue<number>[]
) => (!isUomExcluded ? uomConversionFactors : initialData)?.map(item => {
  return {
    ...item,
    ...(item?.value === id && {
      factor: actionType === CodeActionType.Edit
        ? (isUomExcluded ? item?.factor : defaultUomConversionFactor)
        : item?.factor
    })
  };
}) ?? [];
